import useGetNewPwdIsValid from "@/app/account/hooks/useGetNewPwdIsValid/useGetNewPwdIsValid"
import BasicButton from "@/components/button/BasicButton"
import Dialog from "@/components/dialog/Dialog"
import PasswordField from "@/components/form/passwordField/PasswordField"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import "./changePwdDialog.scss"
import useGetPwdErrMsg from "@/app/account/hooks/useGetPwdErrMsg/useGetPwdErrMsg"
import useGetNewPwdErrMsg from "@/app/account/hooks/useGetNewPwdErrMsg/useGetNewPwdErrMsg"
import { useUpsertPasswordMutation } from "@/hooks/api/useAccountQuery"
import { createAccountStore } from "@/stores/account"
import { createGlobalStore } from "@/stores/global"

export default function ChangePwdDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState<string | undefined>(undefined)
  const [newPassword, setNewPassword] = useState<string | undefined>(undefined)
  const [confirmNewPassword, setConfirmNewPassword] = useState<string | undefined>(undefined)

  const { t } = useTranslation("")
  const { mutate: upsertPassword, error, reset } = useUpsertPasswordMutation()
  const { email } = createAccountStore((state) => state.info) || {}
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)

  const isValidNewPwd = useGetNewPwdIsValid(newPassword, confirmNewPassword)
  const currentPwdValidationMsg = useGetPwdErrMsg(current, undefined)
  const currentPwdErrorTxt = error ? t("rules.passwordIncorrect") : currentPwdValidationMsg
  const newPwdErrorTxt = useGetNewPwdErrMsg(current, newPassword)
  const confirmPwdErrorTxt = useGetPwdErrMsg(newPassword, confirmNewPassword)

  const resetDialog = () => {
    setCurrent(undefined)
    setNewPassword(undefined)
    setConfirmNewPassword(undefined)
    reset() // mutation error 초기화

    onClose()
  }

  const postPasswordReq = () => {
    if (!email || !current || !newPassword) {
      return
    }

    upsertPassword(
      { email, oldPassword: current, newPassword },
      {
        onSuccess: () => {
          setSnackBar({
            message: t("messages.successUpdate"),
            severity: "success",
            open: true,
          })
          resetDialog()
        },
      },
    )
  }

  return (
    <Dialog
      open={open}
      onClose={resetDialog}
      title={t("button.changePassword")}
      maxWidth="600px"
      footer={
        <>
          <BasicButton type="default-outlined" size="md" onClick={resetDialog}>
            <p>{t("button.cancel")}</p>
          </BasicButton>
          <BasicButton
            disabled={!isValidNewPwd || !current}
            type="primary-contained"
            size="md"
            onClick={postPasswordReq}>
            {t("button.save")}
          </BasicButton>
        </>
      }>
      <div className="ChangePwdDialogCont">
        <div>
          <label>{t("currentPassword")}</label>
          <PasswordField
            fullWidth
            value={current}
            placeholder={t("password")}
            onChange={(e) => {
              setCurrent((e.target as HTMLInputElement).value)
              if (error) reset() // 타이핑 시작하면 error 초기화
            }}
            error={Boolean(currentPwdErrorTxt)}
            helperText={currentPwdErrorTxt}
            inputProps={{
              tabIndex: 1,
            }}
          />
        </div>
        <div>
          <label>{t("newPassword")}</label>
          <PasswordField
            fullWidth
            value={newPassword}
            placeholder={t("password")}
            onChange={(e) => setNewPassword((e.target as HTMLInputElement).value)}
            error={Boolean(newPwdErrorTxt)}
            helperText={newPwdErrorTxt || t("messages.hintPassword")}
            inputProps={{
              tabIndex: 2,
            }}
          />
        </div>
        <div>
          <label>{t("confirmPassword")}</label>
          <PasswordField
            fullWidth
            value={confirmNewPassword}
            placeholder={t("password")}
            onChange={(e) => setConfirmNewPassword((e.target as HTMLInputElement).value)}
            error={Boolean(confirmPwdErrorTxt)}
            helperText={confirmPwdErrorTxt}
            inputProps={{
              tabIndex: 3,
            }}
          />
        </div>
      </div>
    </Dialog>
  )
}
